import { useCallback, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import type { JSONContent } from '@tiptap/core';
import type {
  SingleChoiceAnswerBlock,
} from '@course-authoring/shared-types';
import {
  richTextToPlainText,
  useTheme,
} from '@course-authoring/shared-types';

import { InteractionRichTextField } from './InteractionRichTextField';
import {
  createTokenRichTextToolbarStyles,
  EMPTY_RICH_TEXT_DOC,
  HEADING_LEVEL_OPTIONS,
  updateById,
} from './interactionEditorUtils';
import { QuestionSelector } from './questionBank/QuestionSelector';
import {
  QuestionSectionLabel,
  createReadOnlyTextStyle,
  resolveQuestionEditorMode,
} from './questionBank/questionEditorShared';
import { useQuestionTextOverride } from './questionBank/useQuestionTextOverride';
import { FieldOverrideStatus } from './questionBank/FieldOverrideStatus';
import { useFieldOverrideState } from './questionBank/useFieldOverrideState';

export interface SingleChoiceAnswerEditorProps {
  block: SingleChoiceAnswerBlock;
  onChange: (updates: Partial<SingleChoiceAnswerBlock>) => void;
  autoFocus?: boolean;
  /**
   * 'block' (default): lesson-block mode — title controls + QuestionSelector,
   * question content rendered read-only from the resolved block fields.
   * 'questionModal': create-question modal mode — editable question fields,
   * no title controls, no lock toggles, switchable Gated/Ungated mode.
   */
  variant?: 'block' | 'questionModal';
  translationMode?: boolean;
  /** NA-198: when true (question in use in a published/archived lesson), disables removing persisted options without hiding the control. */
  disableOptionRemoval?: boolean;
  /** Ids of options that are actually persisted — only these are locked; options added in this draft (no server id yet) stay removable. */
  lockedOptionIds?: ReadonlySet<string>;
}

type FieldName = 'title' | 'description' | 'correctExplanation';

const editorInnerStyles = `
  .single-choice-editor p {
    margin: 0;
  }
  .single-choice-editor p:not(:last-child) {
    margin-bottom: 0.5em;
  }
`;

// --- Main editor ---

export function SingleChoiceAnswerEditor({
  block,
  onChange,
  autoFocus = false,
  variant = 'block',
  translationMode = false,
  disableOptionRemoval = false,
  lockedOptionIds,
}: SingleChoiceAnswerEditorProps): React.ReactElement {
  const { tokens } = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const [focusedField, setFocusedField] = useState<FieldName | null>(null);

  const { isModal, isManaged, showBlockAuthoringControls } = resolveQuestionEditorMode(
    variant,
    block,
    translationMode,
  );

  const titleLevel = block.titleLevel ?? 'h2';
  const richTextToolbarStyles = useMemo(
    () => createTokenRichTextToolbarStyles(tokens),
    [tokens]
  );

  // NA-313: the correct-answer explanation is the one bank-owned field this
  // block may take ownership of. Field names are passed in, not branched on, so
  // the WCS editor uses the same rules with its own key names.
  const explanationOverride = useFieldOverrideState(block, {
    value: 'correctExplanation',
    marker: 'correctExplanationOverridden',
    bankValue: 'correctExplanationBankValue',
  });

  const handleExplanationChange = useCallback(
    (content: JSONContent) => onChange(explanationOverride.buildEdit(content)),
    [explanationOverride, onChange],
  );

  const handleExplanationRevert = useCallback(
    () => onChange(explanationOverride.buildRevert()),
    [explanationOverride, onChange],
  );

  // NA-384: title, same override model, plain string instead of rich text.
  // See the WCS editor's identical comment for the full reasoning — ownership
  // is only claimed while the bank actually has a title, checked via
  // `titleBankValue` rather than `questionDefinitionId` since the former is
  // reliable immediately after a fresh selection.
  const titleOverride = useFieldOverrideState<SingleChoiceAnswerBlock, string>(block, {
    value: 'title',
    marker: 'titleOverridden',
    bankValue: 'titleBankValue',
    requireBankValue: true,
  });
  const hasBankTitle = block.titleBankValue !== undefined;

  const handleTitleChange = useCallback(
    (next: string) => onChange(
      hasBankTitle && !translationMode ? titleOverride.buildEdit(next) : { title: next },
    ),
    [hasBankTitle, translationMode, titleOverride, onChange],
  );

  const handleTitleRevert = useCallback(
    () => onChange(titleOverride.buildRevert()),
    [titleOverride, onChange],
  );

  // Still needed for the read-only translation-mode display below.
  const descriptionText = richTextToPlainText(block.description);

  // NA-382: question text is authored on the block (or on the bank value in the
  // modal); see useQuestionTextOverride for why the commit is on blur.
  const questionText = useQuestionTextOverride(block, onChange, isModal, translationMode);

  const handleFieldFocus = useCallback((field: FieldName) => {
    setFocusedField(field);
  }, []);

  const handleFieldBlur = useCallback(() => {
    setFocusedField(null);
  }, []);

  // Option handlers
  const handleOptionLabelChange = useCallback(
    (id: string, label: string) => {
      onChange({
        options: updateById(block.options, id, (option) => ({ ...option, label })),
      });
    },
    [block.options, onChange]
  );

  const handleOptionRemove = useCallback(
    (id: string) => {
      const newOptions = block.options.filter((o) => o.id !== id);
      const updates: Partial<SingleChoiceAnswerBlock> = { options: newOptions };
      if (block.correctOptionId === id) {
        updates.correctOptionId = '';
      }
      if (block.healthyOptionId === id) {
        updates.healthyOptionId = '';
      }
      onChange(updates);
    },
    [block.options, block.correctOptionId, block.healthyOptionId, onChange]
  );

  const handleOptionAdd = useCallback(() => {
    onChange({
      options: [
        ...block.options,
        { id: crypto.randomUUID(), label: '' },
      ],
    });
  }, [block.options, onChange]);

  const handleOptionMove = useCallback(
    (index: number, direction: -1 | 1) => {
      const targetIndex = index + direction;
      if (targetIndex < 0 || targetIndex >= block.options.length) return;
      const newOptions = [...block.options];
      const item = newOptions[index];
      const target = newOptions[targetIndex];
      if (!item || !target) return;
      newOptions[index] = target;
      newOptions[targetIndex] = item;
      onChange({ options: newOptions });
    },
    [block.options, onChange]
  );

  const handleGatedToggle = useCallback(() => {
    const newGated = !block.gated;
    const updates: Partial<SingleChoiceAnswerBlock> = { gated: newGated };
    if (newGated) {
      updates.healthyOptionId = undefined;
      updates.correctOptionId = block.correctOptionId ?? '';
      updates.correctExplanation = block.correctExplanation ?? EMPTY_RICH_TEXT_DOC;
    } else {
      updates.correctOptionId = undefined;
      updates.correctExplanation = undefined;
      // NA-313: an ungated block has no explanation, so it must not keep
      // claiming ownership of one. Gating back on starts from inheriting.
      updates.correctExplanationOverridden = undefined;
    }
    onChange(updates);
  }, [block.gated, block.correctOptionId, block.correctExplanation, onChange]);

  const titleTypography = useMemo(() => {
    if (titleLevel === 'h1') return tokens.typography.h1;
    if (titleLevel === 'h3') return tokens.typography.h3;
    return tokens.typography.h2;
  }, [titleLevel, tokens]);

  const styles = useMemo((): Record<string, CSSProperties> => ({
    container: {
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing.xl,
      maxWidth: '100%',
      boxSizing: 'border-box',
    },
    titleLevelControls: {
      display: 'flex',
      alignItems: 'center',
      gap: tokens.spacing.sm,
      marginBottom: tokens.spacing.xs,
    },
    titleLevelLabel: {
      fontSize: '12px',
      fontWeight: 600,
      color: tokens.text.muted,
      textTransform: 'uppercase' as const,
      letterSpacing: '0.05em',
    },
    titleLevelButtonGroup: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: tokens.spacing.xs,
    },
    titleLevelButton: {
      minWidth: '40px',
      padding: `${tokens.spacing.xs} ${tokens.spacing.sm}`,
      borderRadius: tokens.radius.sm,
      border: `1px solid ${tokens.borders.default}`,
      backgroundColor: tokens.backgrounds.app,
      color: tokens.text.primary,
      fontFamily: tokens.typography.fontFamily,
      fontSize: '12px',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'background-color 150ms, color 150ms, border-color 150ms',
    },
    titleLevelButtonActive: {
      backgroundColor: tokens.ui.selectionBorder,
      color: tokens.text.onInverse,
      borderColor: tokens.ui.selectionBorder,
    },
    titleInput: {
      width: '100%',
      border: 'none',
      outline: 'none',
      background: 'transparent',
      fontFamily: tokens.typography.fontFamily,
      fontSize: titleTypography.size,
      fontWeight: titleTypography.weight,
      lineHeight: titleTypography.lineHeight,
      color: tokens.text.primary,
      padding: 0,
    },
    readOnlyText: createReadOnlyTextStyle(tokens),
    descriptionEditor: {
      fontFamily: tokens.typography.fontFamily,
      fontSize: tokens.typography.body.size,
      color: tokens.text.muted,
      lineHeight: tokens.typography.body.lineHeight,
    },
    sectionCard: {
      backgroundColor: tokens.backgrounds.app,
      borderRadius: tokens.radius.md,
      padding: tokens.spacing.lg,
      border: `1px solid ${tokens.borders.default}`,
      boxSizing: 'border-box' as const,
    },
    optionRow: {
      display: 'flex',
      alignItems: 'center',
      gap: tokens.spacing.sm,
      marginBottom: tokens.spacing.sm,
    },
    optionInput: {
      flex: 1,
      border: `1px solid ${tokens.borders.default}`,
      borderRadius: tokens.radius.sm,
      padding: `${tokens.spacing.sm} ${tokens.spacing.md}`,
      fontFamily: tokens.typography.fontFamily,
      fontSize: tokens.typography.body.size,
      color: tokens.text.primary,
      outline: 'none',
      backgroundColor: tokens.backgrounds.app,
      boxSizing: 'border-box' as const,
    },
    moveButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '24px',
      height: '24px',
      background: 'none',
      border: `1px solid ${tokens.borders.default}`,
      cursor: 'pointer',
      color: tokens.text.muted,
      fontSize: '14px',
      borderRadius: tokens.radius.sm,
      flexShrink: 0,
      padding: 0,
      lineHeight: 1,
    },
    moveButtonDisabled: {
      opacity: 0.3,
      cursor: 'default',
    },
    removeButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '28px',
      height: '28px',
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      color: tokens.text.muted,
      fontSize: '18px',
      borderRadius: tokens.radius.sm,
      flexShrink: 0,
    },
    removeButtonDisabled: {
      cursor: 'not-allowed',
      opacity: 0.4,
    },
    addLink: {
      background: 'none',
      border: 'none',
      color: tokens.brand.primary,
      cursor: 'pointer',
      fontFamily: tokens.typography.fontFamily,
      fontSize: tokens.typography.body.size,
      fontWeight: 500,
      padding: 0,
    },
    radioButton: {
      width: '18px',
      height: '18px',
      cursor: isModal ? 'pointer' : 'default',
      flexShrink: 0,
      accentColor: tokens.brand.primary,
    },
    radioLabel: {
      fontSize: '11px',
      fontWeight: 600,
      color: tokens.text.muted,
      textTransform: 'uppercase' as const,
      letterSpacing: '0.05em',
      flexShrink: 0,
      width: '60px',
    },
    toggleContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: tokens.spacing.md,
    },
    toggleTrack: {
      position: 'relative' as const,
      width: '44px',
      height: '24px',
      borderRadius: '12px',
      cursor: isModal ? 'pointer' : 'default',
      transition: 'background-color 150ms',
      border: 'none',
      padding: 0,
      flexShrink: 0,
    },
    toggleThumb: {
      position: 'absolute' as const,
      top: '2px',
      width: '20px',
      height: '20px',
      borderRadius: '50%',
      backgroundColor: '#fff',
      transition: 'left 150ms',
      boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
    },
    toggleLabel: {
      fontSize: tokens.typography.body.size,
      fontWeight: 500,
      color: tokens.text.primary,
      fontFamily: tokens.typography.fontFamily,
    },
    warningBanner: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: tokens.spacing.sm,
      padding: `${tokens.spacing.sm} ${tokens.spacing.md}`,
      borderRadius: tokens.radius.md,
      border: `1px solid ${tokens.ui.workflowDraft}`,
      backgroundColor: `color-mix(in srgb, ${tokens.ui.workflowDraft} 12%, transparent)`,
      fontFamily: tokens.typography.fontFamily,
      fontSize: '13px',
      color: tokens.text.primary,
      lineHeight: 1.4,
    },
    warningIcon: {
      fontSize: '14px',
      color: tokens.ui.workflowDraft,
      marginTop: '2px',
      flexShrink: 0,
    },
    feedbackEditor: {
      fontFamily: tokens.typography.fontFamily,
      fontSize: tokens.typography.body.size,
      color: tokens.text.primary,
      lineHeight: tokens.typography.body.lineHeight,
    },
  }), [isModal, titleTypography, tokens]);

  const modeToggle = (
    <div style={styles.toggleContainer}>
      <button
        type="button"
        onClick={handleGatedToggle}
        disabled={!isModal}
        style={{
          ...styles.toggleTrack,
          backgroundColor: block.gated ? tokens.brand.primary : '#d1d5db',
        }}
        role="switch"
        aria-checked={block.gated}
        aria-label="Gated mode"
      >
        <div
          style={{
            ...styles.toggleThumb,
            left: block.gated ? '22px' : '2px',
          }}
        />
      </button>
      <span style={styles.toggleLabel}>
        {block.gated ? 'Gated — correct answer required to proceed' : 'Ungated — any answer accepted'}
      </span>
    </div>
  );

  return (
    <div ref={containerRef} style={styles.container}>
      <style>{editorInnerStyles}</style>

      {/* Title with heading level selector (block mode only) */}
      {!isModal && (
        <div>
          {showBlockAuthoringControls && (
            <div style={styles.titleLevelControls}>
              <span style={styles.titleLevelLabel}>Title style:</span>
              <div style={styles.titleLevelButtonGroup} role="group" aria-label="Title heading level">
                {HEADING_LEVEL_OPTIONS.map((level) => (
                  <button
                    key={level}
                    type="button"
                    onClick={() => onChange({ titleLevel: level })}
                    style={{
                      ...styles.titleLevelButton,
                      ...(titleLevel === level ? styles.titleLevelButtonActive : {}),
                    }}
                    aria-pressed={titleLevel === level}
                  >
                    {level.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>
          )}
          <input
            type="text"
            value={titleOverride.displayValue ?? ''}
            placeholder="Title"
            onChange={(e) => handleTitleChange(e.target.value)}
            onFocus={() => handleFieldFocus('title')}
            onBlur={handleFieldBlur}
            style={styles.titleInput}
            autoFocus={autoFocus}
          />
          {!translationMode && (
            <FieldOverrideStatus
              isDiverged={titleOverride.isDiverged}
              onRevert={handleTitleRevert}
              fieldLabel="title"
            />
          )}
        </div>
      )}

      {/* Question selection (block mode only) */}
      {showBlockAuthoringControls && (
        <QuestionSelector
          block={block}
          onChange={onChange as (updates: Record<string, unknown>) => void}
        />
      )}

      {(isModal || isManaged) && (
        <>
      {/*
        Question text.

        NA-382: block-overridable, so it is editable in BOTH the create-question
        modal (authoring the bank value) and the lesson block (overriding it) —
        hence no `isModal` gate and `managed={false}`.

        This used to share one `sectionCard` with the Mode row, under a single
        absolutely-positioned "Managed in Question Bank" note covering both.
        Mode (gatedness) IS still bank-owned, so the note could not simply be
        dropped and could not stay where it was without asserting something
        false about the question text — the sections are split instead, the same
        move NA-313 made on Workplace Color Spectrum's results card.

        It was previously read-only PLAIN TEXT in block mode, so rich formatting
        becomes visible here for the first time.
      */}
      {questionText.isEditable ? (
      <div style={isModal ? undefined : styles.sectionCard}>
        <QuestionSectionLabel
          label="Question text"
          managed={false}
          align="split"
          trailing={questionText.showStatus ? (
            <FieldOverrideStatus
              isDiverged={questionText.override.isDiverged}
              onRevert={questionText.onRevert}
              fieldLabel="question text"
            />
          ) : undefined}
        />
        <InteractionRichTextField
          content={questionText.content ?? EMPTY_RICH_TEXT_DOC}
          placeholder="Question or description for the learner..."
          isFocused={focusedField === 'description'}
          onFocus={() => handleFieldFocus('description')}
          onBlur={handleFieldBlur}
          onContentChange={questionText.onContentChange}
          autoFocus={autoFocus}
          containerRef={containerRef}
          editorClassName="tiptap single-choice-editor"
          editorStyle={styles.descriptionEditor}
          toolbarPreset="full"
          toolbarStyles={richTextToolbarStyles}
        />
      </div>
      ) : descriptionText !== '' ? (
        /*
          Translation mode, question text NOT overridden: the field stays Question
          Bank-owned, so it is shown as read-only localized context rather than as
          an editable block field (`lesson-block-translation`). Editing it here
          would let a translation claim ownership, which the default-locale scope
          alone may do.
        */
        <div style={styles.sectionCard}>
          <QuestionSectionLabel label="Question text" managed={isManaged} align="split" />
          <div style={styles.readOnlyText}>{descriptionText}</div>
        </div>
      ) : null}

      {/* Mode (block mode) — still Question Bank-owned, so it keeps the marker. */}
      {!isModal && (
        <div style={styles.sectionCard}>
          <QuestionSectionLabel label="Mode" managed={isManaged} align="split" />
          {modeToggle}
        </div>
      )}

      {/* Gated toggle (modal only — managed mode renders it inside the Question settings card above) */}
      {isModal && (
        <div>
          <QuestionSectionLabel label="Mode" managed={false} />
          {modeToggle}
        </div>
      )}

      {/* Answer options */}
      <div style={styles.sectionCard}>
        <QuestionSectionLabel label="Answer options" managed={isManaged} align="split" />
        {block.options.map((option, index) => (
          <div key={option.id} style={styles.optionRow}>
            <input
              type="radio"
              name={`${block.id}-answer-select`}
              checked={
                block.gated
                  ? block.correctOptionId === option.id
                  : block.healthyOptionId === option.id
              }
              disabled={!isModal}
              onChange={() => {
                if (block.gated) {
                  onChange({ correctOptionId: option.id });
                } else {
                  onChange({ healthyOptionId: option.id });
                }
              }}
              style={styles.radioButton}
              title={block.gated ? 'Mark as correct answer' : 'Mark as healthy answer'}
            />
            <span style={styles.radioLabel}>
              {block.gated ? 'Correct' : 'Healthy'}
            </span>
            {isModal && (
              <>
                <button
                  type="button"
                  style={{
                    ...styles.moveButton,
                    ...(index === 0 ? styles.moveButtonDisabled : {}),
                  }}
                  onClick={() => handleOptionMove(index, -1)}
                  disabled={index === 0}
                  title="Move up"
                  aria-label="Move option up"
                >
                  ▲
                </button>
                <button
                  type="button"
                  style={{
                    ...styles.moveButton,
                    ...(index === block.options.length - 1 ? styles.moveButtonDisabled : {}),
                  }}
                  onClick={() => handleOptionMove(index, 1)}
                  disabled={index === block.options.length - 1}
                  title="Move down"
                  aria-label="Move option down"
                >
                  ▼
                </button>
              </>
            )}
            <input
              type="text"
              value={option.label}
              placeholder="Answer option"
              disabled={!isModal}
              onChange={(e) => handleOptionLabelChange(option.id, e.target.value)}
              style={styles.optionInput}
            />
            {isModal && block.options.length > 2 && (() => {
              const isOptionLocked = disableOptionRemoval && (lockedOptionIds ? lockedOptionIds.has(option.id) : true);
              return (
                <button
                  type="button"
                  style={{ ...styles.removeButton, ...(isOptionLocked ? styles.removeButtonDisabled : {}) }}
                  onClick={() => handleOptionRemove(option.id)}
                  disabled={isOptionLocked}
                  title={isOptionLocked ? "Can't remove options from a question used in a published lesson" : 'Remove option'}
                  onMouseEnter={(e) => {
                    if (!isOptionLocked) e.currentTarget.style.color = '#b91c1c';
                  }}
                  onMouseLeave={(e) => {
                    if (!isOptionLocked) e.currentTarget.style.color = tokens.text.muted;
                  }}
                >
                  ×
                </button>
              );
            })()}
          </div>
        ))}
        {isModal && (
          <button
            type="button"
            style={styles.addLink}
            onClick={handleOptionAdd}
          >
            + Add answer option
          </button>
        )}
      </div>

      {/* Gated correct-answer warning (create modal only) */}
      {isModal && block.gated && !block.correctOptionId && (
        <div style={styles.warningBanner} role="alert">
          <span style={styles.warningIcon} aria-hidden="true">
            <FontAwesomeIcon icon={faTriangleExclamation} style={{ fontSize: 14 }} />
          </span>
          <span>
            You must select a correct answer or learners will be unable to proceed.
          </span>
        </div>
      )}

      {/*
        Correct explanation (gated only).

        NA-313: the one bank-owned field a block may take ownership of, so it is
        editable in BOTH the create-question modal (authoring the bank value) and
        the lesson block (overriding it) — hence no `isModal` gate and
        `managed={false}`. Every sibling field stays read-only and managed.
      */}
      {block.gated && (
        <div style={styles.sectionCard}>
          <QuestionSectionLabel
            label="Correct answer explanation"
            managed={false}
            align="split"
            trailing={!isModal && !translationMode ? (
              <FieldOverrideStatus
                isDiverged={explanationOverride.isDiverged}
                onRevert={handleExplanationRevert}
                fieldLabel="correct answer explanation"
              />
            ) : undefined}
          />
          <InteractionRichTextField
            content={explanationOverride.displayValue ?? EMPTY_RICH_TEXT_DOC}
            placeholder="Explanation shown after the correct answer is submitted..."
            isFocused={focusedField === 'correctExplanation'}
            onFocus={() => handleFieldFocus('correctExplanation')}
            onBlur={handleFieldBlur}
            onContentChange={handleExplanationChange}
            autoFocus={false}
            containerRef={containerRef}
            editorClassName="tiptap single-choice-editor"
            editorStyle={styles.feedbackEditor}
            toolbarPreset="full"
            toolbarStyles={richTextToolbarStyles}
          />
        </div>
      )}
        </>
      )}
    </div>
  );
}

SingleChoiceAnswerEditor.displayName = 'SingleChoiceAnswerEditor';
